// Terrain grid. Owns walkability, height levels (for cliff vision), building
// footprints and resource footprints. Mutating the grid bumps `version`, which
// invalidates cached flow fields in the pathfinder.

import { MAP_W, MAP_H } from './constants.js';

export class Grid {
  constructor(w = MAP_W, h = MAP_H) {
    this.w = w; this.h = h;
    const n = w * h;
    this.height = new Uint8Array(n);   // terrain level, 0 = low, 1 = high
    this.cliff  = new Uint8Array(n);   // static unwalkable terrain (cliff faces / map border)
    this.ramp   = new Uint8Array(n);   // walkable transition between height levels
    this.occ    = new Int32Array(n).fill(-1); // entity id of building/resource occupying this tile
    this.noBuild= new Uint8Array(n);   // resource halos etc.
    this.version = 1;
  }
  idx(x, y) { return y * this.w + x; }
  inBounds(x, y) { return x >= 0 && y >= 0 && x < this.w && y < this.h; }

  heightAt(x, y) { return this.inBounds(x, y) ? this.height[y * this.w + x] : 0; }

  /** Ground passability: no cliff, no building/resource footprint. */
  passable(x, y) {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return false;
    const i = y * this.w + x;
    return this.cliff[i] === 0 && this.occ[i] === -1;
  }
  /** Terrain-only passability, ignoring entities (used for build placement). */
  terrainPassable(x, y) {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return false;
    return this.cliff[y * this.w + x] === 0;
  }
  occupant(x, y) { return this.inBounds(x, y) ? this.occ[y * this.w + x] : -1; }

  /** True if a `w x h` footprint anchored at (x,y) can host a structure. */
  canPlace(x, y, w, h, { ignoreNoBuild = false } = {}) {
    if (x < 0 || y < 0 || x + w > this.w || y + h > this.h) return false;
    const lvl = this.heightAt(x, y);
    for (let j = y; j < y + h; j++) {
      for (let i = x; i < x + w; i++) {
        const k = j * this.w + i;
        if (this.cliff[k]) return false;
        if (this.occ[k] !== -1) return false;
        if (this.ramp[k]) return false;              // never build on a ramp
        if (!ignoreNoBuild && this.noBuild[k]) return false;
        if (this.height[k] !== lvl) return false;    // footprint must be flat
      }
    }
    return true;
  }

  stamp(x, y, w, h, id) {
    for (let j = y; j < y + h; j++)
      for (let i = x; i < x + w; i++)
        if (this.inBounds(i, j)) this.occ[j * this.w + i] = id;
    this.version++;
  }
  unstamp(x, y, w, h) {
    for (let j = y; j < y + h; j++)
      for (let i = x; i < x + w; i++)
        if (this.inBounds(i, j)) this.occ[j * this.w + i] = -1;
    this.version++;
  }
  markNoBuild(x, y, w, h, pad = 1) {
    for (let j = y - pad; j < y + h + pad; j++)
      for (let i = x - pad; i < x + w + pad; i++)
        if (this.inBounds(i, j)) this.noBuild[j * this.w + i] = 1;
  }

  /**
   * Recompute cliff faces: a low tile touching a higher tile is a cliff face
   * unless it is flagged as a ramp. Produces SC-style one-tile cliff bands that
   * force all level transitions through ramps.
   */
  bakeCliffs() {
    const { w, h, height, ramp } = this;
    const out = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        // The border band must be symmetric under the map's 180-degree rotation
        // (tile x maps to tile w-x). A band of {0,1} and {w-2,w-1} is NOT: it
        // pairs interior tile 2 with border tile w-2, handing one player a tile
        // of extra room along the edge. {0,1,2} and {w-2,w-1} pairs correctly.
        if (x < 3 || y < 3 || x >= w - 2 || y >= h - 2) { out[i] = 1; continue; }
        if (ramp[i]) continue;
        const lvl = height[i];
        let higher = false;
        for (let dy = -1; dy <= 1 && !higher; dy++)
          for (let dx = -1; dx <= 1; dx++) {
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            if (height[ny * w + nx] > lvl && !ramp[ny * w + nx]) { higher = true; break; }
          }
        if (higher) out[i] = 1;
      }
    }
    // preserve explicitly-authored blockers
    for (let i = 0; i < out.length; i++) if (this.cliff[i] === 2) out[i] = 2;
    this.cliff = out;
    this.version++;
  }

  /** Nearest passable tile to (x,y) via expanding ring search. */
  nearestPassable(x, y, maxR = 24) {
    x = Math.max(0, Math.min(this.w - 1, Math.round(x)));
    y = Math.max(0, Math.min(this.h - 1, Math.round(y)));
    if (this.passable(x, y)) return { x, y };
    for (let r = 1; r <= maxR; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const nx = x + dx, ny = y + dy;
          if (this.passable(nx, ny)) return { x: nx, y: ny };
        }
      }
    }
    return { x, y };
  }
}
